import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { MdDownload, MdEdit } from 'react-icons/md';
import { request } from '../../config/helpers/axios-instance';
import CreatePaymentItemModal from './CreatePaymentItemModal';
import EditPaymentItemModal from './EditPaymentItemModal';

import PaymentItemDetailsModal from './PaymentItemDetailsModal';
import CreatePaymentLinkModal from './CreatePaymentLinkModal';
import PaymentInvoicesPrint from './PaymentInvoicesPrint';
import {
    fetchPaymentItem,
    getInvoiceFileName,
    hasStoredInvoicePdf,
    isServicePaymentItem,
    persistServiceInvoicePdf,
    renderServiceInvoicePdf,
} from './serviceInvoicePdf';
import Modal from '../Modal';
import { useNavigate } from 'react-router-dom';
import { getUserRoles, formatGstSerial } from '../../utils/helpers';
import { ROLES } from '../../utils/constants';

const INELIGIBLE_FOR_SERIAL = ['CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED'];

const RELEASE_HOLD_GST_ACTIONS = false;

const PaymentsItemsTable = ({ paymentItems = [], refetch, orderId, orderData, quotationId, quotationData }) => {
    const navigate = useNavigate();
    /** Quotation edit page: quote already converted — manage payment items on the order only */
    const quotationHasLinkedOrder = Boolean(quotationId && quotationData?.orderId);
    const [isCreating, setIsCreating] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
    const [detailsModalItem, setDetailsModalItem] = useState(null);
    const [editModalItem, setEditModalItem] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showQuoteSearchPanel, setShowQuoteSearchPanel] = useState(false);
    const [quoteSearchValue, setQuoteSearchValue] = useState('');
    const [quoteSearchType, setQuoteSearchType] = useState('id');
    const [quotationSearchResults, setQuotationSearchResults] = useState([]);
    const [isSearchingQuotations, setIsSearchingQuotations] = useState(false);
    const [loadingQuotationId, setLoadingQuotationId] = useState(null);
    const [loadedQuotation, setLoadedQuotation] = useState(null);
    const [loadedQuotationPaymentItems, setLoadedQuotationPaymentItems] = useState([]);
    const [invoicePrintPayload, setInvoicePrintPayload] = useState(null);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [generatingPdfId, setGeneratingPdfId] = useState(null);

    // Feature A: GST serial issuance + credit notes
    const [issuingId, setIssuingId] = useState(null);
    const [confirmIssueItem, setConfirmIssueItem] = useState(null);
    const [confirmCreditNoteItem, setConfirmCreditNoteItem] = useState(null); // holds the originalPaymentItem
    const [syncingEvitalId, setSyncingEvitalId] = useState(null);
    const [isCreatingDefaultItems, setIsCreatingDefaultItems] = useState(false);

    const userRoles = getUserRoles();
    const canIssueSerial = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPER_SUPPORT].some((r) => userRoles.includes(r));
    const canCreateCreditNote = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_SUPPORT].some((r) => userRoles.includes(r));
    const canSyncEvital = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_SUPPORT].some((r) => userRoles.includes(r));
    const canEditDeleteItem = [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.PHARMACIST, ROLES.SUPPORT, ROLES.SUPER_SUPPORT].some((r) => userRoles.includes(r));

    const isRefunded = (status) => status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED';

    // Even after a GST serial is issued, the paymentItem stays editable through the 5th
    // of the month AFTER its billedAt month (grace period to fix mistakes before that
    // month's GST filing closes it out). After that cutoff, edits are blocked.
    const isWithinSerialEditGracePeriod = (billedAt) => {
        if (!billedAt) return false;
        const billed = new Date(billedAt);
        if (Number.isNaN(billed.getTime())) return false;
        const cutoff = new Date(billed.getFullYear(), billed.getMonth() + 1, 5, 23, 59, 59, 999);
        return new Date() <= cutoff;
    };

    // Function to get status pill color based on payment status
    const getStatusColor = (status) => {
        switch (status) {
            case 'DRAFT':
                return 'bg-gray-400 text-white-50';
            case 'GENERATED':
                return 'bg-blue-500 text-white-50';
            case 'PARTIALLY_GENERATED':
                return 'bg-blue-400 text-white-50';
            case 'OVERDUE_GENERATED':
                return 'bg-orange-500 text-white-50';
            case 'PAID':
                return 'bg-green-500 text-white-50';
            case 'PARTIALLY_PAID':
                return 'bg-yellow-500 text-white-50';
            case 'OVERDUE':
                return 'bg-orange-600 text-white-50';
            case 'CANCELLED':
                return 'bg-gray-500 text-white-50';
            case 'REFUNDED':
                return 'bg-purple-500 text-white-50';
            case 'PARTIALLY_REFUNDED':
                return 'bg-purple-400 text-white-50';
            default:
                return 'bg-gray-400 text-white-50';
        }
    };

    const getStatusBorderColor = (status) => {
        switch (status) {
            case 'PAID': return 'border-green-500';
            case 'GENERATED': return 'border-blue-500';
            case 'PARTIALLY_GENERATED': return 'border-blue-400';
            case 'OVERDUE_GENERATED': return 'border-orange-500';
            case 'PARTIALLY_PAID': return 'border-yellow-500';
            case 'OVERDUE': return 'border-orange-600';
            case 'DRAFT': return 'border-gray-400';
            case 'CANCELLED': return 'border-red-500';
            case 'REFUNDED': return 'border-purple-500';
            case 'PARTIALLY_REFUNDED': return 'border-purple-400';
            default: return 'border-gray-300';
        }
    };

    const createPaymentItem = async (payload) => {
        setIsCreating(true);
        try {
            const res = await request({
                url: 'paymentItem/admin',
                method: 'POST',
                data: payload,
            });

            if (res?.data?.success) {
                toast.success('Payment item created successfully');
                setShowCreateModal(false);
                refetch();
                // Render from the SERVER row — the backend rewrites the GST split from the
                // order address, so the form values may not match what was stored. Refetch
                // again afterwards so the row picks up invoicePdfUrl.
                const created = res.data.data;
                if (isServicePaymentItem(created)) {
                    await storeServiceInvoicePdf(created);
                    refetch();
                }
            } else {
                toast.error(res?.data?.message || 'Failed to create payment item');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to create payment item');
        } finally {
            setIsCreating(false);
        }
    };

    /**
     * Render → upload → persist the SERVICE invoice PDF for an existing row. Never throws: the
     * payment item is already saved, so a PDF failure is a warning and "Generate PDF" repairs it.
     */
    const storeServiceInvoicePdf = async (paymentItem) => {
        setGeneratingPdfId(paymentItem.id);
        try {
            await persistServiceInvoicePdf(paymentItem, getPaymentItemSourceData(paymentItem));
            toast.success('Service invoice PDF generated');
            return true;
        } catch (error) {
            toast.warn(`Payment item saved, but the invoice PDF could not be generated — use "Generate PDF" to retry. (${error?.message || 'unknown error'})`);
            return false;
        } finally {
            setGeneratingPdfId(null);
        }
    };

    /** Manual (re)generate — also the backfill path for SERVICE items created before PDFs were stored. */
    const regenerateServiceInvoicePdf = async (paymentItem) => {
        setGeneratingPdfId(paymentItem.id);
        try {
            const fresh = await fetchPaymentItem(paymentItem.id);
            await persistServiceInvoicePdf(fresh, getPaymentItemSourceData(paymentItem));
            toast.success('Service invoice PDF generated');
            refetch();
        } catch (error) {
            toast.error(error?.message || 'Failed to generate invoice PDF');
        } finally {
            setGeneratingPdfId(null);
        }
    };

    const updatePaymentItem = async (paymentItemId, payload) => {
        try {
            const res = await request({
                url: `paymentItem/admin/id/${paymentItemId}`,
                method: 'PUT',
                data: payload,
            });
            if (res?.data?.success) {
                toast.success('Payment item updated successfully');
                setEditModalItem(null);
                // The PUT response is not the updated row; fetch it so the PDF reflects the
                // server-side GST re-split and any edited notes.
                if (isServicePaymentItem(editModalItem)) {
                    try {
                        const fresh = await fetchPaymentItem(paymentItemId);
                        await storeServiceInvoicePdf(fresh);
                    } catch (error) {
                        toast.warn('Payment item updated, but the invoice PDF could not be refreshed — use "Generate PDF".');
                    }
                }
                refetch();
            } else {
                toast.error(res?.data?.message || 'Failed to update payment item');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update payment item');
        }
    };

    const issueGstSerial = async (paymentItem) => {
        setConfirmIssueItem(null);
        setIssuingId(paymentItem.id);
        try {
            const res = await request({
                url: `paymentItem/admin/issueSerial/${paymentItem.id}`,
                method: 'POST',
            });
            if (res?.data?.success) {
                const d = res.data.data;
                toast.success(
                    d.alreadyIssued
                        ? `Already issued: serial ${d.serialNumber}`
                        : `GST serial issued: ${d.serialNumber} (FY ${d.financialYear})`
                );
                refetch();
            } else {
                toast.error(res?.data?.error || 'Failed to issue GST serial');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to issue GST serial');
        } finally {
            setIssuingId(null);
        }
    };

    const createCreditNote = async (originalPaymentItem) => {
        try {
            const res = await request({
                url: 'paymentItem/creditNote',
                method: 'POST',
                data: { originalPaymentItemId: originalPaymentItem.id },
            });
            if (res?.data?.success) {
                toast.success('Credit note created and GST serial issued');
                setConfirmCreditNoteItem(null);
                refetch();
            } else if (res?.data?.data?.code === 'CREDIT_NOTE_ALREADY_EXISTS') {
                toast.error('A credit note already exists for this invoice.');
            } else {
                toast.error(res?.data?.error || 'Failed to create credit note');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to create credit note');
        }
    };

    const createDefaultPaymentItems = async () => {
        if (!window.confirm('Create default payment items (Medicine + Service) for this order from eVital?')) return;
        setIsCreatingDefaultItems(true);
        try {
            const res = await request({
                url: `userOrder/admin/createDefaultPaymentItems/${orderId}`,
                method: 'POST',
            });
            if (res?.data?.success) {
                toast.success('Default payment items created successfully');
                refetch();
            } else {
                toast.error(res?.data?.error || 'Failed to create default payment items');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to create default payment items');
        } finally {
            setIsCreatingDefaultItems(false);
        }
    };

    const syncEvitalAmounts = async (paymentItem) => {
        setSyncingEvitalId(paymentItem.id);
        try {
            const res = await request({
                url: `paymentItem/admin/syncEvitalAmounts/${paymentItem.id}`,
                method: 'POST',
            });
            if (res?.data?.success) {
                toast.success('Amounts synced from eVital');
                refetch();
            } else {
                toast.error(res?.data?.error || 'Failed to sync from eVital');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to sync from eVital');
        } finally {
            setSyncingEvitalId(null);
        }
    };

    const PAYMENT_LINK_ELIGIBLE_STATUSES = ['DRAFT', 'PARTIALLY_GENERATED', 'PARTIALLY_PAID'];
    const itemsArray = Array.isArray(paymentItems) ? paymentItems : Object.values(paymentItems || {});
    const externalPaymentItems = loadedQuotationPaymentItems.map((item) => ({
        ...item,
        isExternalPaymentItem: true,
    }));
    const paymentItemsById = new Map();

    itemsArray.forEach((item) => {
        paymentItemsById.set(item.id, { ...item, isExternalPaymentItem: false });
    });
    externalPaymentItems.forEach((item) => {
        if (!paymentItemsById.has(item.id)) {
            paymentItemsById.set(item.id, item);
        }
    });

    const combinedPaymentItems = Array.from(paymentItemsById.values());

    // Group credit notes under their originating invoice (Change 3b)
    const cnsByOriginal = new Map();
    const nonCnItems = [];
    for (const item of combinedPaymentItems) {
        if (item.documentType === 'CREDIT_NOTE' && item.originalPaymentItemId != null) {
            const arr = cnsByOriginal.get(item.originalPaymentItemId) || [];
            arr.push(item);
            cnsByOriginal.set(item.originalPaymentItemId, arr);
        } else {
            nonCnItems.push(item);
        }
    }
    nonCnItems.sort((a, b) => (b.id || 0) - (a.id || 0));
    cnsByOriginal.forEach((arr) => arr.sort((a, b) => (a.id || 0) - (b.id || 0)));
    const sortedPaymentItems = [];
    for (const item of nonCnItems) {
        sortedPaymentItems.push(item);
        const cns = cnsByOriginal.get(item.id);
        if (cns) { sortedPaymentItems.push(...cns); cnsByOriginal.delete(item.id); }
    }
    cnsByOriginal.forEach((arr) => sortedPaymentItems.push(...arr));
    const REVERSED = ['CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED'];
    const invoiceRows = sortedPaymentItems.filter((i) => i.documentType !== 'CREDIT_NOTE');
    const grossInvoiced = invoiceRows.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
    const refundedTotal = invoiceRows.filter((i) => REVERSED.includes(i.status)).reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
    const netPayable = grossInvoiced - refundedTotal;
    const creditNotesTotal = sortedPaymentItems.filter((i) => i.documentType === 'CREDIT_NOTE').reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
    // Credit notes are reversals — a payment can never be collected against them.
    const isSelectableForPayment = (item) =>
        PAYMENT_LINK_ELIGIBLE_STATUSES.includes(item.status) && item.documentType !== 'CREDIT_NOTE';
    const selectableItems = sortedPaymentItems.filter(isSelectableForPayment);
    const selectedItems = sortedPaymentItems.filter((item) => selectedIds.has(item.id));
    const allSelectableSelected = selectableItems.length > 0 && selectableItems.every((item) => selectedIds.has(item.id));
    const selectedTotalAmount = selectedItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
    const loadedQuotationSelectableItems = loadedQuotationPaymentItems.filter(isSelectableForPayment);
    const loadedQuotationSelectedItems = loadedQuotationPaymentItems.filter((item) => selectedIds.has(item.id));
    const loadedQuotationSelectedTotal = loadedQuotationSelectedItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);

    const searchQuotations = async () => {
        const search = quoteSearchValue.trim();
        if (!search) {
            toast.warn('Please enter a quotation search value');
            return;
        }

        setIsSearchingQuotations(true);
        try {
            const res = await request({
                url: 'quotation/admin',
                method: 'GET',
                params: {
                    page: 1,
                    size: 10,
                    quoteId: quoteSearchType === 'id' ? search : undefined,
                    quoteValue: quoteSearchType === 'amount' ? search : undefined,
                    patientName: quoteSearchType === 'name' ? search : undefined,
                    userName: quoteSearchType === 'name' ? search : undefined,
                },
            });

            const rows = res?.data?.data?.rows || [];
            setQuotationSearchResults(rows);
            if (!rows.length) {
                toast.info('No quotations found');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to search quotations');
        } finally {
            setIsSearchingQuotations(false);
        }
    };

    const fetchQuotationPaymentItems = async (quoteId) => {
        const res = await request({
            url: `quotation/admin/orderDetails/${quoteId}`,
            method: 'GET',
        });

        const quotation = res?.data?.data;
        const quotePaymentItems = Array.isArray(quotation?.paymentItems)
            ? quotation.paymentItems
            : Object.values(quotation?.paymentItems || {});

        return {
            quotation,
            paymentItems: quotePaymentItems,
        };
    };

    const loadQuotationPaymentItems = async (quote) => {
        if (!quote?.id) return;
        if (Number(quote.id) === Number(quotationId)) {
            toast.info('This quotation is already shown in the payment items table');
            return;
        }

        setLoadingQuotationId(quote.id);
        try {
            const { quotation, paymentItems: quotePaymentItems } = await fetchQuotationPaymentItems(quote.id);

            setLoadedQuotation(quotation || quote);
            setLoadedQuotationPaymentItems(quotePaymentItems);
            if (!quotePaymentItems.length) {
                toast.info('No payment items found for the selected quotation');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load quotation payment items');
        } finally {
            setLoadingQuotationId(null);
        }
    };

    const refreshLoadedQuotationPaymentItems = async () => {
        if (!loadedQuotation?.id) return;

        try {
            const { quotation, paymentItems: quotePaymentItems } = await fetchQuotationPaymentItems(loadedQuotation.id);
            setLoadedQuotation(quotation || loadedQuotation);
            setLoadedQuotationPaymentItems(quotePaymentItems);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to refresh other quote payment items');
        }
    };

    const clearLoadedQuotation = () => {
        const loadedIds = new Set(loadedQuotationPaymentItems.map((item) => item.id));
        setLoadedQuotation(null);
        setLoadedQuotationPaymentItems([]);
        setSelectedIds((prev) => new Set([...prev].filter((id) => !loadedIds.has(id))));
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (allSelectableSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableItems.map((item) => item.id)));
        }
    };

    const submitPaymentCollection = async (payload) => {
        const isCod = payload.mode === 'COD';
        const isQr = payload.mode === 'QR';
        const isB2b = payload.mode === 'B2B';
        const isInApp = payload.mode === 'IN_APP';
        const isCoins = payload.mode === 'COINS';
        const isPayuOpenLink = payload.mode === 'PAYU_OPEN_LINK';
        const isManual = isCod || isQr || isB2b || isInApp || isCoins;
        const manualBody = {
            paymentItemIds: payload.paymentItemIds,
            userId: payload.userId,
            amount: payload.amount,
            currency: payload.currency || 'INR',
            ...(isCoins ? { debitWallet: Boolean(payload.debitWallet) } : {}),
        };
        try {
            const res = await request({
                url: isCod
                    ? 'payment/admin/addCashPaymentOnPaymentItems'
                    : isQr
                      ? 'payment/admin/addQrPaymentOnPaymentItems'
                      : isB2b
                        ? 'payment/admin/addB2bPaidPaymentOnPaymentItems'
                        : isInApp
                          ? 'payment/admin/addInAppPaymentOnPaymentItems'
                          : isCoins
                            ? 'payment/admin/addCoinPaymentOnPaymentItems'
                            : isPayuOpenLink
                              ? 'payment/admin/addOpenLinkPaymentOnPaymentItems'
                              : 'payment/admin/generatePaymentLinkOnPaymentItems',
                method: 'POST',
                data: isManual
                    ? manualBody
                    : {
                        paymentItemIds: payload.paymentItemIds,
                        userId: payload.userId,
                        amount: payload.amount,
                        gateway: payload.gateway || 'PAYU',
                        currency: payload.currency || 'INR',
                        user: payload.user,
                    },
            });
            if (res?.data?.success) {
                toast.success(
                    isCod          ? 'Pending COD payment created' :
                    isQr           ? 'QR payment recorded successfully' :
                    isB2b          ? 'B2B payment recorded successfully' :
                    isInApp        ? 'In-app payment recorded successfully' :
                    isCoins        ? 'Coin payment recorded successfully' :
                    isPayuOpenLink ? 'PayU open link created successfully' :
                                     'Payment link created successfully'
                );
                setShowPaymentLinkModal(false);
                setSelectedIds(new Set());
                await Promise.all([
                    refetch(),
                    refreshLoadedQuotationPaymentItems(),
                ]);
                return true;
            }
            toast.error(res?.data?.message || (
                isCod          ? 'Failed to create pending COD payment' :
                isQr           ? 'Failed to record QR payment' :
                isB2b          ? 'Failed to record B2B payment' :
                isInApp        ? 'Failed to record in-app payment' :
                isCoins        ? 'Failed to record coin payment' :
                isPayuOpenLink ? 'Failed to create PayU open link' :
                                 'Failed to create payment link'
            ));
            return false;
        } catch (error) {
            toast.error(error?.response?.data?.message || (
                isCod          ? 'Failed to create pending COD payment' :
                isQr           ? 'Failed to record QR payment' :
                isB2b          ? 'Failed to record B2B payment' :
                isInApp        ? 'Failed to record in-app payment' :
                isCoins        ? 'Failed to record coin payment' :
                isPayuOpenLink ? 'Failed to create PayU open link' :
                                 'Failed to create payment link'
            ));
            return false;
        }
    };

    const getPaymentItemSourceData = (paymentItem) => {
        if (paymentItem.isExternalPaymentItem) {
            return loadedQuotation || {};
        }
        return orderData || quotationData || {};
    };

    /** One row per quotation (or fallback key) for mapUserPatient display in payment link modal */
    const involvedQuotesMapUsers = useMemo(() => {
        const seen = new Map();
        selectedItems.forEach((item) => {
            const source = getPaymentItemSourceData(item);
            const qid = item.quotationId ?? source?.id;
            const key = qid != null ? `q:${qid}` : `o:${item.orderId ?? 'na'}:pi:${item.id}`;
            if (seen.has(key)) return;
            seen.set(key, {
                rowKey: key,
                quotationId: qid,
                orderId: item.orderId,
                mapUserPatient: source?.mapUserPatient,
            });
        });
        return Array.from(seen.values());
    }, [selectedItems, loadedQuotation, orderData, quotationData]);

    const buildAddressPrintPayload = () => {
        const source = orderData || quotationData;
        if (!source?.address) {
            return null;
        }
        return {
            address: source.address,
            orderId: orderId || source.id,
            billNo: source?.additionalDetails?.billNumber ?? null,
            mapUserPatient: source?.mapUserPatient,
        };
    };

    const printMedicineInvoice = (paymentItem) => {
        setInvoicePrintPayload({
            paymentItems: [paymentItem],
            sourceData: getPaymentItemSourceData(paymentItem),
            addressPrint: buildAddressPrintPayload(),
        });
    };

    // Browser-rendered fallback for items without a stored PDF (DELIVERY, or SERVICE rows
    // created before PDFs were persisted).
    const downloadGeneratedInvoice = async (paymentItem) => {
        setDownloadingInvoiceId(paymentItem.id);
        try {
            const doc = await renderServiceInvoicePdf(paymentItem, getPaymentItemSourceData(paymentItem));
            doc.save(getInvoiceFileName(paymentItem));
        } catch (error) {
            toast.error('Failed to download invoice');
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    const downloadInvoice = (paymentItem) => {
        if (paymentItem.invoiceType === 'MEDICINE') {
            printMedicineInvoice(paymentItem);
            return;
        }
        // Stored PDF wins: it is the document that was generated when the item was saved.
        // invoicePdfUrl is a fresh SAS URL signed by the backend on read.
        if (paymentItem.invoicePdfUrl) {
            window.open(paymentItem.invoicePdfUrl, '_blank', 'noopener');
            return;
        }

        downloadGeneratedInvoice(paymentItem);
    };

    const CreateButton = () => (
        <button
            className="bg-blue-600 text-white-50 !text-white-50 font-medium py-2 px-4 rounded shadow hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowCreateModal(true)}
            disabled={isCreating}
        >
            {isCreating ? 'Creating...' : '+ Create Payment Item'}
        </button>
    );

    // GST serial cell renderer
    const renderGstSerial = (paymentItem) => {
        if (paymentItem.invoiceType === 'MEDICINE') {
            return <span className="text-gray-400 text-xs">N/A (eVital)</span>;
        }
        const serial = formatGstSerial(paymentItem);
        if (serial) {
            return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-medium ${paymentItem.documentType === 'CREDIT_NOTE' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                    {serial}
                </span>
            );
        }
        return <span className="text-gray-400 text-xs italic">Not issued</span>;
    };

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4 -z-5">
            <div className="flex justify-between items-center mb-4 gap-3">
                <div>
                    <h3 className="text-lg font-semibold">Payment Items [{sortedPaymentItems.length}]</h3>
                    {(orderId || quotationId) && (
                        <p className="mt-0.5 text-xs text-gray-500">
                            {orderId && <span>Order <span className="font-medium text-gray-700">#{orderId}</span></span>}
                            {orderId && quotationId && <span className="mx-1">·</span>}
                            {quotationId && <span>Quote <span className="font-medium text-gray-700">#{quotationId}</span></span>}
                        </p>
                    )}
                    {quotationHasLinkedOrder && (
                        <p className="mt-1 text-sm text-gray-600">
                            This quotation has been converted to an order. Create and manage payment items on the order page.
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {orderId && itemsArray.length === 0 && canSyncEvital && !quotationHasLinkedOrder && (
                        <button
                            type="button"
                            onClick={createDefaultPaymentItems}
                            disabled={isCreatingDefaultItems}
                            className="bg-teal-600 text-white-50 font-medium py-2 px-4 rounded shadow hover:bg-teal-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isCreatingDefaultItems ? 'Creating...' : '+ Create Default Payment Items'}
                        </button>
                    )}
                    {quotationHasLinkedOrder ? (
                        <button
                            type="button"
                            className="bg-blue-600 text-white-50 !text-white-50 font-medium py-2 px-4 rounded shadow hover:bg-blue-700 transition-colors text-sm"
                            onClick={() => navigate(`/order/edit/${quotationData.orderId}`)}
                        >
                            Go to Order
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => selectedItems.length > 0 && setShowPaymentLinkModal(true)}
                                disabled={selectedItems.length === 0}
                                className="bg-green-600 text-white-50 font-medium py-2 px-4 rounded shadow hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {selectedItems.length > 0
                                    ? `Collect payment (${selectedItems.length} items - ₹ ${selectedTotalAmount.toFixed(2)})`
                                    : 'Collect payment'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowQuoteSearchPanel((prev) => !prev)}
                                className="bg-white border border-blue-600 text-blue-600 font-medium py-2 px-4 rounded shadow-sm hover:bg-blue-50 transition-colors text-sm"
                            >
                                {showQuoteSearchPanel ? 'Hide Quote Search' : '+ Add Items From Another Quote'}
                            </button>
                            <CreateButton />
                        </>
                    )}
                </div>
            </div>

            {showQuoteSearchPanel && !quotationHasLinkedOrder && (
                <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-blue-900">Add payment items from another quote</div>
                            <div className="mt-1 text-sm text-blue-700">
                                Search a quotation, load its eligible payment items, then select them from the table below.
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowQuoteSearchPanel(false)}
                            className="rounded border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                        >
                            Close
                        </button>
                    </div>

                    <div className="rounded-xl border border-blue-100 bg-white p-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                value={quoteSearchValue}
                                onChange={(e) => setQuoteSearchValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') searchQuotations();
                                }}
                                className="min-w-[260px] flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 font-sans text-sm"
                                placeholder="Search quote by selected field"
                            />
                            <select
                                value={quoteSearchType}
                                onChange={(e) => setQuoteSearchType(e.target.value)}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 font-sans text-sm"
                            >
                                <option value="id">Quote ID</option>
                                <option value="amount">Quote Amount</option>
                                <option value="name">Patient/User Name</option>
                            </select>
                            <button
                                type="button"
                                onClick={searchQuotations}
                                disabled={isSearchingQuotations}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white-50 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isSearchingQuotations ? 'Searching...' : 'Search Quotes'}
                            </button>
                        </div>
                    </div>

                    {quotationSearchResults.length > 0 && (
                        <div className="mt-4">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
                                Search Results
                            </div>
                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {quotationSearchResults.map((quote) => (
                                    <div key={quote.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-semibold text-green-600">Quote #{quote.id}</div>
                                                <div className="mt-1 text-sm text-gray-600">
                                                    {quote.mapUserPatient?.User?.name || 'Unknown user'}
                                                </div>
                                            </div>
                                            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                                                {quote.status || '-'}
                                            </span>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Quote Total</span>
                                            <span className="font-semibold">₹ {quote.total ?? '-'}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => loadQuotationPaymentItems(quote)}
                                            disabled={loadingQuotationId === quote.id}
                                            className="mt-4 w-full rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white-50 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {loadingQuotationId === quote.id ? 'Loading items...' : 'View Payment Items'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {loadedQuotation && (
                <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-green-800">
                                Added quote #{loadedQuotation.id} payment items
                            </div>
                            <div className="mt-1 text-sm text-green-700">
                                {loadedQuotationPaymentItems.length} total items, {loadedQuotationSelectableItems.length} eligible, {loadedQuotationSelectedItems.length} selected
                                {loadedQuotationSelectedItems.length > 0 ? ` - ₹ ${loadedQuotationSelectedTotal.toFixed(2)}` : ''}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={clearLoadedQuotation}
                            className="rounded-lg border border-green-600 bg-white px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-100"
                        >
                            Remove Quote Items
                        </button>
                    </div>
                </div>
            )}

            {sortedPaymentItems.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full">
                        <thead>
                            <tr className="text-left h-12 font-sans border-b-2 border-gray-100">
                                <th className="pl-3 font-sans text-left px-2 w-12">
                                    {!quotationHasLinkedOrder && (
                                        <input
                                            type="checkbox"
                                            checked={allSelectableSelected}
                                            onChange={toggleSelectAll}
                                            disabled={!selectableItems.length}
                                            className="w-4 h-4 rounded border-gray-300"
                                        />
                                    )}
                                </th>
                                <th className="pl-3 font-sans text-left px-2">Id</th>
                                <th className="pl-3 font-sans text-left px-2">InvoiceType</th>
                                <th className="pl-3 font-sans text-left px-2">SubTotal</th>
                                <th className="pl-3 font-sans text-left px-2">Total</th>
                                <th className="pl-3 font-sans text-left px-2">Status</th>
                                <th className="pl-3 font-sans text-left px-2">GST Serial</th>
                                <th className="pl-3 font-sans text-left px-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="border-collapse">
                            {sortedPaymentItems.map((paymentItem, index) => {
                                const refunded = isRefunded(paymentItem.status);
                                const serialIssued = paymentItem.serialNumber != null;
                                const serialEditable = !serialIssued || isWithinSerialEditGracePeriod(paymentItem.billedAt);
                                const isIneligibleStatus = INELIGIBLE_FOR_SERIAL.includes(paymentItem.status);
                                const isMedicine = paymentItem.invoiceType === 'MEDICINE';
                                const isCreditNote = paymentItem.documentType === 'CREDIT_NOTE';

                                const originalItem = isCreditNote && paymentItem.originalPaymentItemId != null
                                    ? paymentItemsById.get(paymentItem.originalPaymentItemId)
                                    : null;
                                const originalSerial = originalItem ? formatGstSerial(originalItem) : null;

                                const showIssueButton =
                                    !isMedicine &&
                                    paymentItem.serialNumber == null &&
                                    !isIneligibleStatus &&
                                    canIssueSerial &&
                                    !paymentItem.isExternalPaymentItem &&
                                    Boolean(orderData);

                                // A CN against this item means its billed amount is already being
                                // reversed on paper -- editing the original after that would drift
                                // it out of sync with the CN that was issued against its old values.
                                const hasExistingCreditNote = combinedPaymentItems.some(
                                    (pi) => pi.documentType === 'CREDIT_NOTE'
                                        && pi.originalPaymentItemId === paymentItem.id
                                        && pi.status !== 'CANCELLED'
                                );

                                const canManageItem = !refunded && !paymentItem.isExternalPaymentItem && !quotationHasLinkedOrder && serialEditable && canEditDeleteItem && !hasExistingCreditNote;

                                const showCreditNoteButton =
                                    !isMedicine &&
                                    !isCreditNote &&
                                    paymentItem.documentType === 'INVOICE' &&
                                    paymentItem.serialNumber != null &&
                                    !isIneligibleStatus &&
                                    canCreateCreditNote &&
                                    !paymentItem.isExternalPaymentItem &&
                                    !hasExistingCreditNote;

                                const showSyncEvitalButton =
                                    isMedicine &&
                                    paymentItem.refEntityType === 'inventoryId' &&
                                    Boolean(paymentItem.refEntityValue) &&
                                    canSyncEvital &&
                                    !paymentItem.isExternalPaymentItem;

                                const statusBorderColor = isCreditNote ? 'border-orange-300' : getStatusBorderColor(paymentItem.status);

                                return (
                                    <tr
                                        key={paymentItem.id || index}
                                        className={`text-left border-t border-gray-100 h-14 font-sans border-l-4 ${statusBorderColor} ${refunded ? 'bg-gray-100/60 opacity-75' : 'hover:bg-gray-50'} ${paymentItem.isExternalPaymentItem ? 'bg-green-50/40' : ''} ${isCreditNote ? 'bg-orange-50/30' : ''}`}
                                    >
                                        <td className="pl-3 py-4 font-sans text-left">
                                            {!quotationHasLinkedOrder && isSelectableForPayment(paymentItem) && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(paymentItem.id)}
                                                    onChange={() => toggleSelect(paymentItem.id)}
                                                    className="w-4 h-4 rounded border-gray-300"
                                                />
                                            )}
                                        </td>
                                        <td className={`py-4 font-sans text-left ${isCreditNote ? 'pl-8' : 'pl-3'}`}>
                                            <div className="flex flex-col gap-0.5">
                                                <span>
                                                    {isCreditNote && <span className="text-orange-400 mr-1">↳</span>}
                                                    <span className="font-semibold text-green-600 bg-gray-100 px-2 py-1 rounded">
                                                        {paymentItem.id}
                                                    </span>
                                                </span>
                                                {paymentItem.isExternalPaymentItem && (
                                                    <>
                                                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 w-fit">
                                                            Other Quote
                                                        </span>
                                                        {paymentItem.quotationId && (
                                                            <span className="text-xs text-gray-400">Q#{paymentItem.quotationId}</span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`py-4 font-sans text-left ${isCreditNote ? 'pl-8' : 'pl-3'}`}>
                                            <div className="flex flex-col gap-0.5">
                                                <span>{paymentItem.invoiceType || '-'}</span>
                                                {isCreditNote && (
                                                    <span className="text-xs font-medium text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded w-fit">
                                                        Credit Note
                                                    </span>
                                                )}
                                                {isCreditNote && paymentItem.originalPaymentItemId != null && (
                                                    <span className="text-xs text-orange-500 mt-0.5">
                                                        {'↳ against #'}{paymentItem.originalPaymentItemId}{originalSerial ? ` (${originalSerial})` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="pl-3 py-4 font-sans text-left">
                                            {paymentItem.subTotal || '-'}
                                        </td>

                                        <td className="pl-3 py-4 font-sans text-left">
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                                                    {paymentItem.totalAmount || '-'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => !refunded && setDetailsModalItem(paymentItem)}
                                                    disabled={refunded}
                                                    className={`p-0.5 rounded transition-colors ${refunded ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}
                                                    title="View details"
                                                >
                                                    <HiOutlineInformationCircle className="w-6 h-6" />
                                                </button>
                                            </span>
                                        </td>
                                        <td className="pl-3 py-4 font-sans text-left">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(paymentItem.status)}`}
                                            >
                                                {paymentItem.status}
                                            </span>
                                        </td>
                                        <td className="pl-3 py-4 font-sans text-left">
                                            {renderGstSerial(paymentItem)}
                                        </td>
                                        <td className="pl-3 py-4 font-sans text-left">
                                            <div className="inline-flex flex-wrap items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => canManageItem && setEditModalItem(paymentItem)}
                                                    disabled={!canManageItem}
                                                    className={`inline-flex items-center gap-1.5 p-1.5 rounded transition-colors ${!canManageItem ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                                                    title={
                                                        paymentItem.isExternalPaymentItem
                                                            ? 'Open that quotation to edit this payment item'
                                                            : (hasExistingCreditNote
                                                                ? 'A credit note already exists against this item — edit the credit note instead'
                                                                : (serialIssued && !serialEditable
                                                                    ? 'GST serial issued and past the 5th-of-next-month edit window'
                                                                    : 'Edit payment item'))
                                                    }
                                                >
                                                    <MdEdit className="w-6 h-6" />
                                                    <span className="text-sm">Edit</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => downloadInvoice(paymentItem)}
                                                    disabled={downloadingInvoiceId === paymentItem.id}
                                                    className="inline-flex items-center gap-1.5 p-1.5 rounded text-green-600 hover:text-green-800 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Download invoice"
                                                >
                                                    <MdDownload className="w-6 h-6" />
                                                    <span className="text-sm">
                                                        {downloadingInvoiceId === paymentItem.id ? 'Downloading...' : 'Invoice'}
                                                    </span>
                                                </button>
                                                {isServicePaymentItem(paymentItem) && !paymentItem.isExternalPaymentItem && canEditDeleteItem && (
                                                    <button
                                                        type="button"
                                                        onClick={() => regenerateServiceInvoicePdf(paymentItem)}
                                                        disabled={generatingPdfId === paymentItem.id}
                                                        className={`inline-flex items-center gap-1.5 p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium ${hasStoredInvoicePdf(paymentItem) ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-50' : 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'}`}
                                                        title={hasStoredInvoicePdf(paymentItem) ? 'Re-render and replace the stored invoice PDF' : 'No stored invoice PDF yet — generate and store one'}
                                                    >
                                                        {generatingPdfId === paymentItem.id
                                                            ? 'Generating…'
                                                            : hasStoredInvoicePdf(paymentItem) ? 'Regenerate PDF' : 'Generate PDF'}
                                                    </button>
                                                )}
                                                {/* RELEASE-HOLD: "Issue GST Serial" button hidden for this release (gated by RELEASE_HOLD_GST_ACTIONS). Backend + handler intact; flip the flag to re-enable. */}
                                                {!RELEASE_HOLD_GST_ACTIONS && showIssueButton && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmIssueItem(paymentItem)}
                                                        disabled={issuingId === paymentItem.id}
                                                        className="inline-flex items-center gap-1.5 p-1.5 rounded text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                                        title="Issue GST serial number"
                                                    >
                                                        {issuingId === paymentItem.id ? 'Issuing…' : '# Issue Serial'}
                                                    </button>
                                                )}
                                                {/* RELEASE-HOLD: "Create Credit Note" (CN) button hidden for this release (gated by RELEASE_HOLD_GST_ACTIONS). Backend + handler intact; flip the flag to re-enable. */}
                                                {!RELEASE_HOLD_GST_ACTIONS && showCreditNoteButton && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmCreditNoteItem(paymentItem)}
                                                        className="inline-flex items-center gap-1.5 p-1.5 rounded text-orange-600 hover:text-orange-800 hover:bg-orange-50 transition-colors text-sm font-medium"
                                                        title="Create credit note against this invoice"
                                                    >
                                                        CN
                                                    </button>
                                                )}
                                                {showSyncEvitalButton && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (window.confirm('Refresh amounts from eVital? This overwrites the current totals.')) {
                                                                syncEvitalAmounts(paymentItem);
                                                            }
                                                        }}
                                                        disabled={syncingEvitalId === paymentItem.id}
                                                        className="inline-flex items-center gap-1.5 p-1.5 rounded text-teal-600 hover:text-teal-800 hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                                        title="Refresh amounts from eVital"
                                                    >
                                                        {syncingEvitalId === paymentItem.id ? 'Syncing…' : '↻ Sync eVital'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-200 bg-gray-50">
                                <td colSpan={4} className="pl-3 py-3 font-sans text-right text-gray-600 pr-2 font-semibold">
                                    Net Payable
                                </td>
                                <td className="pl-3 py-3 font-sans text-left">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-sm bg-gray-200 px-2 py-1 rounded font-semibold">
                                            ₹ {netPayable.toFixed(2)}
                                        </span>
                                        <span className="text-xs text-gray-400 font-normal whitespace-nowrap">
                                            Invoiced ₹{grossInvoiced.toFixed(2)} · Refunded −₹{refundedTotal.toFixed(2)} · CNs ₹{creditNotesTotal.toFixed(2)}
                                        </span>
                                    </div>
                                </td>
                                <td colSpan={3} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            ) : (
                <p className="text-gray-500 text-center py-8">
                    {quotationHasLinkedOrder
                        ? 'No payment items on this quotation. Use Go to Order to manage payment items.'
                        : 'No payment items found'}
                </p>
            )}
            {showCreateModal && (
                <CreatePaymentItemModal
                    orderId={orderId}
                    quotationId={quotationId}
                    onClose={() => setShowCreateModal(false)}
                    onSave={(payload) => createPaymentItem(payload)}
                />
            )}
            {editModalItem && (
                <EditPaymentItemModal
                    paymentItem={editModalItem}
                    onClose={() => setEditModalItem(null)}
                    onSave={(payload) => updatePaymentItem(editModalItem.id, payload)}
                />
            )}
            {showPaymentLinkModal && (
                <CreatePaymentLinkModal
                    selectedItems={selectedItems}
                    orderData={orderData || quotationData}
                    involvedQuotesMapUsers={involvedQuotesMapUsers}
                    onClose={() => setShowPaymentLinkModal(false)}
                    onCreate={submitPaymentCollection}
                />
            )}
            {detailsModalItem && (
                <PaymentItemDetailsModal
                    paymentItem={detailsModalItem}
                    onClose={() => setDetailsModalItem(null)}
                />
            )}
            {invoicePrintPayload && (
                <PaymentInvoicesPrint
                    paymentItems={invoicePrintPayload.paymentItems}
                    sourceData={invoicePrintPayload.sourceData}
                    addressPrint={invoicePrintPayload.addressPrint}
                    onDone={() => setInvoicePrintPayload(null)}
                />
            )}

            {/* Confirm modal for Issue GST Serial */}
            {confirmIssueItem && (
                <Modal title="Issue GST Serial" onClose={() => setConfirmIssueItem(null)}>
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Issue a GST serial number for payment item{' '}
                            <strong>#{confirmIssueItem.id}</strong> ({confirmIssueItem.invoiceType})?
                        </p>
                        <p className="text-sm text-red-600 font-medium">
                            This action mints the next serial in the sequence and cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setConfirmIssueItem(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => issueGstSerial(confirmIssueItem)}
                                className="px-4 py-2 text-sm font-medium text-white-50 bg-indigo-600 rounded hover:bg-indigo-700 transition-colors"
                            >
                                Issue Serial
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Confirm modal for Create Credit Note */}
            {confirmCreditNoteItem && (
                <Modal title="Create Credit Note" onClose={() => setConfirmCreditNoteItem(null)}>
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Create a full credit note for{' '}
                            <strong>{formatGstSerial(confirmCreditNoteItem) || `#${confirmCreditNoteItem.id}`}</strong>{' '}
                            — ₹{confirmCreditNoteItem.totalAmount}?
                        </p>
                        <p className="text-sm text-red-600 font-medium">
                            This mirrors the full invoice amount and cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setConfirmCreditNoteItem(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => createCreditNote(confirmCreditNoteItem)}
                                className="px-4 py-2 text-sm font-medium text-white-50 bg-orange-600 rounded hover:bg-orange-700 transition-colors"
                            >
                                Create Credit Note
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

PaymentsItemsTable.propTypes = {
    paymentItems: PropTypes.array.isRequired,
    refetch: PropTypes.func.isRequired,
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderData: PropTypes.object,
    quotationData: PropTypes.object
};

export default PaymentsItemsTable;
